<script setup>
// AIVIO timers: «Таймеры» settings page (rules list + log). Backend: fixes/timers/*.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import AivioTimersAPI from 'dashboard/api/aivioTimers';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import TimerLog from './TimerLog.vue';
import TimerRuleDialog from './TimerRuleDialog.vue';
import {
  DEFAULT_MAX_MINUTES,
  actionSummary,
  conditionSentence,
  extractErrors,
} from './timerUtils';

const LOG_LIMIT = 100;
const MAX_ACTIONS = 10;

const store = useStore();
const { t, locale } = useI18n();

const accountId = useMapGetter('getCurrentAccountId');
const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');

const rules = ref([]);
const meta = ref({});
const isLoading = ref(true);
const loadFailed = ref(false);
const activeTab = ref(0);
const logEntries = ref([]);
const isLogLoading = ref(false);
const busyIds = ref({});
const ruleToDelete = ref(null);
const isDeleting = ref(false);
const ruleDialogRef = ref(null);
const deleteDialogRef = ref(null);

const tabs = computed(() => [
  { key: 'rules', label: t('AIVIO_TIMERS.TABS.RULES') },
  { key: 'log', label: t('AIVIO_TIMERS.TABS.LOG') },
]);

const isKillSwitchOn = computed(() => meta.value.enabled === false);
const maxRules = computed(() => meta.value.max_rules || 20);
const canCreate = computed(
  () => !isLoading.value && rules.value.length < maxRules.value
);

const byId = list =>
  Object.fromEntries((list || []).map(item => [String(item.id), item]));
const inboxById = computed(() => byId(inboxes.value));
const agentById = computed(() => byId(agents.value));
const teamById = computed(() => byId(teams.value));

const inboxNames = computed(() =>
  Object.fromEntries(
    Object.entries(inboxById.value).map(([id, inbox]) => [id, inbox.name])
  )
);

const agentName = id => {
  const agent = agentById.value[String(id)];
  return agent ? agent.available_name || agent.name : '';
};
const teamName = id => teamById.value[String(id)]?.name || '';

const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({ value: inbox.id, label: inbox.name }))
);
const labelOptions = computed(() =>
  (labels.value || []).map(label => ({
    value: label.title,
    label: label.title,
  }))
);
const agentOptions = computed(() =>
  (agents.value || []).map(agent => ({
    value: agent.id,
    label: agent.available_name || agent.name,
  }))
);
const teamOptions = computed(() =>
  (teams.value || []).map(team => ({ value: team.id, label: team.name }))
);

const inboxScope = rule => {
  const ids = rule.inbox_ids || [];
  if (!ids.length) return t('AIVIO_TIMERS.ALL_INBOXES');
  const names = ids.map(id => inboxNames.value[String(id)] || `#${id}`);
  if (names.length <= 3) return names.join(', ');
  return t('AIVIO_TIMERS.INBOXES_MORE', {
    names: names.slice(0, 3).join(', '),
    n: names.length - 3,
  });
};

const actionsText = rule =>
  (rule.actions || [])
    .map(action =>
      actionSummary(action, t, { agent: agentName, team: teamName })
    )
    .join(' · ');

const formatDate = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleString(locale.value, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return date.toLocaleString();
  }
};

const showErrors = (error, fallbackKey) => {
  const messages = extractErrors(error);
  useAlert(messages.length ? messages.join('; ') : t(fallbackKey));
};

const fetchRules = async () => {
  isLoading.value = true;
  loadFailed.value = false;
  try {
    const { data } = await AivioTimersAPI.get();
    rules.value = data.payload || [];
    meta.value = data.meta || {};
  } catch (error) {
    loadFailed.value = true;
    showErrors(error, 'AIVIO_TIMERS.ERRORS.LOAD');
  } finally {
    isLoading.value = false;
  }
};

const fetchLog = async () => {
  isLogLoading.value = true;
  try {
    const { data } = await AivioTimersAPI.getLog(LOG_LIMIT);
    logEntries.value = data.payload || [];
  } catch (error) {
    showErrors(error, 'AIVIO_TIMERS.ERRORS.LOAD_LOG');
  } finally {
    isLogLoading.value = false;
  }
};

const onTabChanged = tab => {
  activeTab.value = tabs.value.findIndex(item => item.key === tab.key);
  if (tab.key === 'log') fetchLog();
};

const replaceRule = rule => {
  const index = rules.value.findIndex(item => item.id === rule.id);
  if (index === -1) rules.value = [...rules.value, rule];
  else rules.value.splice(index, 1, rule);
};

const toggleRule = async (rule, enabled) => {
  busyIds.value = { ...busyIds.value, [rule.id]: true };
  try {
    const { data } = await AivioTimersAPI.update(rule.id, { enabled });
    replaceRule(data.payload);
    useAlert(
      enabled ? t('AIVIO_TIMERS.TURNED_ON') : t('AIVIO_TIMERS.TURNED_OFF')
    );
  } catch (error) {
    showErrors(error, 'AIVIO_TIMERS.ERRORS.SAVE');
  } finally {
    const rest = { ...busyIds.value };
    delete rest[rule.id];
    busyIds.value = rest;
  }
};

const deleteTitle = computed(() =>
  t('AIVIO_TIMERS.DELETE_CONFIRM.TITLE', {
    name: ruleToDelete.value?.name || '',
  })
);

const openCreate = () => ruleDialogRef.value?.open(null);
const openEdit = rule => ruleDialogRef.value?.open(rule);

const askDelete = rule => {
  ruleToDelete.value = rule;
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  const rule = ruleToDelete.value;
  if (!rule) return;
  isDeleting.value = true;
  try {
    await AivioTimersAPI.delete(rule.id);
    rules.value = rules.value.filter(item => item.id !== rule.id);
    useAlert(t('AIVIO_TIMERS.DELETED'));
    deleteDialogRef.value?.close();
  } catch (error) {
    showErrors(error, 'AIVIO_TIMERS.ERRORS.DELETE');
  } finally {
    isDeleting.value = false;
  }
};

onMounted(() => {
  // Pickers in the form; dispatching is cheap and mirrors automation settings.
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  fetchRules();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('AIVIO_TIMERS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader :title="t('AIVIO_TIMERS.TITLE')">
        <template #description>
          {{ t('AIVIO_TIMERS.DESCRIPTION') }}
        </template>
        <template #tabs>
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTab"
            @tab-changed="onTabChanged"
          />
        </template>
        <template #actions>
          <Button
            v-if="activeTab === 0"
            icon="i-lucide-plus"
            size="sm"
            :label="t('AIVIO_TIMERS.NEW')"
            :disabled="!canCreate"
            :title="
              canCreate ? '' : t('AIVIO_TIMERS.LIMIT_REACHED', { n: maxRules })
            "
            @click="openCreate"
          />
          <Button
            v-else
            icon="i-lucide-refresh-cw"
            color="slate"
            size="sm"
            :label="t('AIVIO_TIMERS.LOG.REFRESH')"
            :is-loading="isLogLoading"
            :disabled="isLogLoading"
            @click="fetchLog"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <Banner v-if="isKillSwitchOn" color="amber" class="mb-4">
        {{ t('AIVIO_TIMERS.KILL_SWITCH') }}
      </Banner>

      <template v-if="activeTab === 0">
        <div
          v-if="loadFailed"
          class="flex flex-col items-center gap-3 py-20 text-n-slate-12"
        >
          <span>{{ t('AIVIO_TIMERS.ERRORS.LOAD') }}</span>
          <Button
            color="slate"
            size="sm"
            :label="t('AIVIO_TIMERS.RETRY')"
            @click="fetchRules"
          />
        </div>
        <div
          v-else-if="!rules.length"
          class="flex flex-col items-center gap-3 py-20 text-center"
        >
          <span class="text-base text-n-slate-12">
            {{ t('AIVIO_TIMERS.EMPTY') }}
          </span>
          <span class="max-w-md text-sm text-n-slate-11">
            {{ t('AIVIO_TIMERS.EMPTY_HINT') }}
          </span>
        </div>
        <ul v-else class="flex flex-col divide-y divide-n-weak">
          <li
            v-for="rule in rules"
            :key="rule.id"
            class="flex items-start justify-between gap-4 py-4"
          >
            <div class="flex flex-col min-w-0 gap-1">
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-heading-3 text-n-slate-12 break-words">
                  {{ rule.name }}
                </span>
                <Label
                  compact
                  :color="rule.mode === 'live' ? 'teal' : 'slate'"
                  :label="
                    rule.mode === 'live'
                      ? t('AIVIO_TIMERS.MODES.LIVE')
                      : t('AIVIO_TIMERS.MODES.LOG')
                  "
                />
                <Label
                  v-if="!rule.enabled"
                  compact
                  color="amber"
                  :label="t('AIVIO_TIMERS.DISABLED')"
                />
              </div>
              <span class="text-body-main text-n-slate-12">
                {{ conditionSentence(rule, t) }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ inboxScope(rule) }}
                <template v-if="rule.respect_working_hours !== false">
                  · {{ t('AIVIO_TIMERS.WORKING_HOURS_SHORT') }}
                </template>
              </span>
              <span class="text-sm text-n-slate-11 break-words">
                {{ t('AIVIO_TIMERS.THEN') }} {{ actionsText(rule) }}
              </span>
              <span
                v-if="rule.enabled && rule.activated_at"
                class="text-xs text-n-slate-10"
              >
                {{
                  t('AIVIO_TIMERS.ACTIVE_SINCE', {
                    date: formatDate(rule.activated_at),
                  })
                }}
              </span>
            </div>
            <div class="flex items-center flex-shrink-0 gap-2">
              <Switch
                :model-value="!!rule.enabled"
                :disabled="!!busyIds[rule.id]"
                :class="{ 'opacity-60': busyIds[rule.id] }"
                @update:model-value="value => toggleRule(rule, value)"
              />
              <Button
                icon="i-lucide-pencil"
                variant="ghost"
                color="slate"
                size="sm"
                :title="t('AIVIO_TIMERS.EDIT')"
                @click="openEdit(rule)"
              />
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                color="ruby"
                size="sm"
                :title="t('AIVIO_TIMERS.DELETE')"
                @click="askDelete(rule)"
              />
            </div>
          </li>
        </ul>
      </template>

      <template v-else>
        <woot-loading-state
          v-if="isLogLoading && !logEntries.length"
          :message="t('AIVIO_TIMERS.LOADING')"
        />
        <TimerLog
          v-else
          :entries="logEntries"
          :account-id="accountId"
          :inbox-names="inboxNames"
        />
      </template>

      <TimerRuleDialog
        ref="ruleDialogRef"
        :account-id="accountId"
        :max-minutes="meta.max_minutes || DEFAULT_MAX_MINUTES"
        :max-actions="MAX_ACTIONS"
        :allowed-actions="meta.actions || []"
        :inbox-options="inboxOptions"
        :label-options="labelOptions"
        :agent-options="agentOptions"
        :team-options="teamOptions"
        @saved="replaceRule"
      />

      <Dialog
        ref="deleteDialogRef"
        type="alert"
        :title="deleteTitle"
        :description="t('AIVIO_TIMERS.DELETE_CONFIRM.DESCRIPTION')"
        :confirm-button-label="t('AIVIO_TIMERS.DELETE_CONFIRM.CONFIRM')"
        :is-loading="isDeleting"
        @confirm="confirmDelete"
      />
    </template>
  </SettingsLayout>
</template>
